import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FeedbackReport } from '@cloudsign/shared';
import type { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useSite } from '../../lib/site';
import { useListState } from '../../lib/useList';
import { ExportButton } from '../../components/ExportButton';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Alert, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { DateRangePicker, isRangeValid } from '../../components/phase2';
import type { DateRange } from '../../components/phase2';
import { reportsKey, useActiveGroups, useTenantZone } from './api';
import { addDays, daysBetween, todayIn } from '../../lib/zoneTime';

type Report = z.infer<typeof FeedbackReport>;
type Summary = Report['bySite'][number];

const PAGE_SIZE = 25;
const MAX_DAYS = 366;

const oneDecimal = (n: number): string => new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

/** Stars for a rating. The number is always written out too, so the stars are decoration. */
function Rating({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span aria-hidden="true" className="text-amber-600">
        {'★'.repeat(Math.max(0, Math.min(5, Math.round(value))))}
        <span className="text-slate-300">{'★'.repeat(5 - Math.max(0, Math.min(5, Math.round(value))))}</span>
      </span>
      <span>{t('rpt.fb.outOf', { rating: value })}</span>
    </span>
  );
}

/** Five small bars, one per rating from 1 to 5; the counts are also in text for screen readers. */
function Distribution({ counts }: { counts: Summary['distribution'] }) {
  const max = Math.max(1, ...counts);
  return (
    <div>
      <div aria-hidden="true" className="flex h-6 items-end gap-0.5">
        {counts.map((c, i) => (
          <span key={i} title={t('rpt.fb.distItem', { rating: i + 1, count: c })} className="w-3 rounded-sm bg-brand" style={{ height: `${Math.max(8, (c / max) * 100)}%`, opacity: c === 0 ? 0.25 : 1 }} />
        ))}
      </div>
      <span className="sr-only">{counts.map((c, i) => t('rpt.fb.distItem', { rating: i + 1, count: c })).join(', ')}</span>
    </div>
  );
}

function SummaryTable({ caption, firstColumn, rows }: { caption: string; firstColumn: string; rows: Summary[] }) {
  return (
    <TableWrap caption={caption}>
      <thead className="border-b border-slate-200 bg-slate-50">
        <tr>
          <Th>{firstColumn}</Th>
          <Th>{t('rpt.fb.col.responses')}</Th>
          <Th>{t('rpt.fb.col.average')}</Th>
          <Th>{t('rpt.fb.col.spread')}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <EmptyRow cols={4} />
        ) : (
          rows.map((r) => (
            <tr key={r.key}>
              <td className="px-4 py-2 font-medium text-slate-900">{r.label}</td>
              <td className="px-4 py-2">{r.responses}</td>
              <td className="px-4 py-2">{r.responses === 0 ? '-' : <Rating value={Number(oneDecimal(r.average))} />}</td>
              <td className="px-4 py-2">
                <Distribution counts={r.distribution} />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </TableWrap>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-600">{label}</p>
    </div>
  );
}

/** Reports > Feedback: how visitors rated their visit, per site and per host, with every response below. */
export function FeedbackReportPage() {
  const zone = useTenantZone();
  const { sites } = useSite();
  const groups = useActiveGroups();
  const list = useListState();
  const [siteId, setSiteId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [range, setRange] = useState<DateRange>(() => {
    const today = todayIn(zone);
    return { from: addDays(today, -29), to: today };
  });
  const complete = range.from !== '' && range.to !== '' && isRangeValid(range);
  const tooLong = complete && daysBetween(range.from, range.to) + 1 > MAX_DAYS;
  const ready = complete && !tooLong;
  const filters = { siteId: siteId || undefined, groupId: groupId || undefined, from: range.from, to: range.to };
  const query = { ...filters, page: list.page, pageSize: PAGE_SIZE };
  const q = useQuery({
    queryKey: [...reportsKey, 'feedback', query],
    enabled: ready,
    queryFn: () => api.get('/feedback/report', { query, schema: FeedbackReport }),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const rate = d && d.sent > 0 ? Math.round((d.responses / d.sent) * 100) : null;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <PageHeader title={t('rpt.fb.title')} description={t('rpt.fb.description')} actions={<ExportButton path="/feedback/report/export" query={filters} fileName="feedback" disabled={!ready} />} />

      <FilterBar columns="sm:grid-cols-2">
        <SelectField
          label={t('rpt.filter.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('rpt.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('rpt.filter.group')}
          value={groupId}
          onChange={(e) => {
            setGroupId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('rpt.filter.allGroups')}</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <div className="sm:col-span-2">
          <DateRangePicker
            label={t('rpt.filter.dates')}
            value={range}
            today={new Date(`${todayIn(zone)}T12:00:00`)}
            onChange={(r) => {
              setRange(r);
              list.resetPage();
            }}
          />
        </div>
      </FilterBar>

      {!complete ? <Alert kind="info">{t('rpt.range.needBoth')}</Alert> : null}
      {tooLong ? <Alert>{t('rpt.range.tooLong', { days: MAX_DAYS })}</Alert> : null}

      {!ready ? null : !d && q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : d ? (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Tile label={t('rpt.fb.tile.sent')} value={String(d.sent)} />
            <Tile label={t('rpt.fb.tile.responses')} value={String(d.responses)} />
            <Tile label={t('rpt.fb.tile.rate')} value={rate === null ? '-' : t('rpt.fb.percent', { value: rate })} />
            <Tile label={t('rpt.fb.tile.average')} value={d.average === null ? '-' : oneDecimal(d.average)} />
          </div>

          <h2 className="text-lg font-semibold text-slate-900">{t('rpt.fb.bySite')}</h2>
          <SummaryTable caption={t('rpt.fb.bySite')} firstColumn={t('rpt.col.site')} rows={d.bySite} />

          <h2 className="text-lg font-semibold text-slate-900">{t('rpt.fb.byHost')}</h2>
          <SummaryTable caption={t('rpt.fb.byHost')} firstColumn={t('rpt.col.host')} rows={d.byHost} />

          <h2 className="text-lg font-semibold text-slate-900">{t('rpt.fb.responses')}</h2>
          <TableWrap caption={t('rpt.fb.responses')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('rpt.fb.col.received')}</Th>
                <Th>{t('rpt.col.site')}</Th>
                <Th>{t('rpt.col.host')}</Th>
                <Th>{t('rpt.fb.col.visitor')}</Th>
                <Th>{t('rpt.fb.col.rating')}</Th>
                <Th>{t('rpt.fb.col.comment')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.items.length === 0 ? (
                <EmptyRow cols={6} />
              ) : (
                d.items.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="whitespace-nowrap px-4 py-2">{formatDateTime(r.receivedAt, zone)}</td>
                    <td className="px-4 py-2">{r.siteName}</td>
                    <td className="px-4 py-2">{r.hostName ?? t('rpt.fb.noHost')}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{r.visitorName}</td>
                    <td className="px-4 py-2">
                      <Rating value={r.rating} />
                    </td>
                    <td className="max-w-md whitespace-pre-wrap px-4 py-2">{r.comment || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={d.total} onPage={list.setPage} />
        </>
      ) : null}
    </div>
  );
}
